import useGetMyAccountPipelines from '@/api/queries/me/useGetMyAccountPipelines';
import { useUserHasPermission } from '@/hooks/usePermissions';
import { ProjectsContext } from '@/ui/components/contextProviders/ProjectsContextProvider';
import { Menu, MenuButton } from '@headlessui/react';
import { Button, DropdownMenuItems } from '@payaca/ambrosia';
import { PipelinesPermissions } from '@payaca/permissions/pipelines/pipelines.permissions';
import UntitledIcon from '@payaca/untitled-icons';
import React, { useContext, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';

function PipelineNavigationDropdown() {
  const { createPipeline } = useContext(ProjectsContext);
  const { pipelineId } = useParams<{ pipelineId: string }>();
  const history = useHistory();
  const canCreatePipeline = useUserHasPermission({
    permissions: [PipelinesPermissions.ADD_PIPELINE],
  });
  const canEditPipeline = useUserHasPermission({
    permissions: [PipelinesPermissions.EDIT_PIPELINE],
  });

  const { data: pipelines } = useGetMyAccountPipelines();

  const selectedPipeline = useMemo(() => {
    if (!pipelines || pipelines.length === 0) {
      return { label: 'Default', color: '#99b1cc' };
    }

    const pipeline = pipelines.find((p) => p.publicId === pipelineId);

    if (pipeline) {
      return { label: pipeline.title, color: pipeline.colour };
    }
    // Fallback to first pipeline or default
    const defaultPipeline = pipelines[0];
    return defaultPipeline
      ? { label: defaultPipeline.title, color: defaultPipeline.colour }
      : { label: 'Default', color: '#99b1cc' };
  }, [pipelines, pipelineId]);

  const renderPipelines = () => {
    return (pipelines ?? []).map((pipeline) => ({
      label: (
        <div className="flex items-center gap-2">
          <UntitledIcon
            name={'circle.4'}
            className="w-3"
            style={{ color: pipeline.colour }}
          />
          <span className="ml-2">{pipeline.title}</span>
        </div>
      ),
      onClick: () => {
        history.push(`/pipelines/${pipeline.publicId}`);
      },
      selected: pipeline.publicId === pipelineId,
    }));
  };

  return (
    <Menu>
      <MenuButton
        as={Button}
        variant="ghost"
        colour="black"
        size="xs"
        className="-m-2"
      >
        <div className="flex items-center gap-2 typography:prose">
          <UntitledIcon
            name={'circle.4'}
            className="w-5"
            style={{ color: selectedPipeline.color }}
          />
          <h3>{selectedPipeline.label}</h3>
        </div>
        <UntitledIcon name={'chevron-selector-vertical'} className="w-4" />
      </MenuButton>
      <DropdownMenuItems
        anchor={'start'}
        className="w-60 outline-none"
        items={[
          {
            heading: <span>Pipelines ({pipelines?.length ?? 0})</span>,
            items: [...renderPipelines()],
          },
          {
            items: [
              {
                icon: 'plus-circle',
                label: 'Add Pipeline',
                onClick: () => {
                  createPipeline();
                },
                disabled: !canCreatePipeline,
                disabledReasons: [
                  "You don't have permission to create pipelines.",
                ],
              },
              {
                icon: 'settings-02',
                label: 'Manage Pipeline',
                onClick: () => {
                  history.push(`/pipelines/${pipelineId}/config`);
                },
                disabled: !canEditPipeline,
                disabledReasons: [
                  "You don't have permission to edit pipelines.",
                ],
              },
            ],
          },
          {
            items: [
              {
                label: (
                  <span className="flex items-center justify-between">
                    View all Projects
                    {/* TODO implement keyboard shortcut */}
                    {/* <span>{getShortcutKey('⌘+A', 'Ctrl+A')}</span> */}
                  </span>
                ),
                onClick: () => {
                  history.push(
                    `/deals?displayType=table&initialRequestData=${encodeURIComponent(
                      JSON.stringify({ pipelineId: null })
                    )}`
                  );
                },
              },
            ],
          },
        ]}
      />
    </Menu>
  );
}

export default PipelineNavigationDropdown;
